import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { fetchDashboardMetrics, DashboardApiError } from '../services/dashboardApi';
import type { DashboardMetrics, DashboardFilters } from '../types/dashboard';
import { useAuth } from '@/shared/hooks/useAuth';
import { apiService } from '../services/api';

export interface UseDashboardMetricsOptions {
  refetchInterval?: number;
  enabled?: boolean;
  filters?: DashboardFilters;
}

export interface UseDashboardMetricsResult {
  data: DashboardMetrics | undefined;
  isLoading: boolean;
  isError: boolean;
  error: DashboardApiError | null;
  isRefetching: boolean;
  refetch: () => void;
  refresh: () => void;
  lastUpdated: Date | null;
}

const DASHBOARD_QUERY_KEY = ['dashboard', 'metrics'];

/**
 * Hook for managing dashboard metrics data with React Query
 */
export function useDashboardMetrics(options: UseDashboardMetricsOptions = {}): UseDashboardMetricsResult {
  const { 
    refetchInterval = 5 * 60 * 1000, // 5 minutes default
    enabled = true,
    filters
  } = options;
  
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Create query key that includes filters for cache invalidation
  const queryKey = [...DASHBOARD_QUERY_KEY, filters];

  const {
    data,
    isLoading,
    isError,
    error,
    isRefetching,
    refetch,
    dataUpdatedAt
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const token = apiService.getToken();
      if (!token) {
        throw new DashboardApiError('No authentication token available', 401, 'NO_TOKEN');
      }
      
      try {
        // Try to use the apiService method first (for consistency)
        return await apiService.getDashboardMetrics();
      } catch {
        // Fallback to direct API call if needed
        return await fetchDashboardMetrics(token);
      }
    },
    enabled: enabled && !!user,
    refetchInterval: refetchInterval > 0 ? refetchInterval : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error instanceof DashboardApiError && error.status === 401) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Manual refresh function
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });
    refetch();
  }, [queryClient, refetch]);

  // Re-fetch when user changes (e.g., login/logout)
  useEffect(() => {
    if (user && enabled) {
      refetch();
    }
  }, [user, enabled, refetch]);

  // Handle authentication errors by clearing cache
  useEffect(() => {
    if (error instanceof DashboardApiError && error.status === 401) {
      queryClient.clear();
    }
  }, [error, queryClient]);

  return {
    data,
    isLoading,
    isError,
    error: error instanceof DashboardApiError ? error : null,
    isRefetching,
    refetch,
    refresh,
    lastUpdated: dataUpdatedAt ? new Date(dataUpdatedAt) : null,
  };
}

/**
 * Hook to prefetch dashboard data
 */
export function usePrefetchDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useCallback(() => {
    const token = apiService.getToken();
    if (token && user) {
      queryClient.prefetchQuery({
        queryKey: DASHBOARD_QUERY_KEY,
        queryFn: () => fetchDashboardMetrics(token),
        staleTime: 2 * 60 * 1000,
      });
    }
  }, [user, queryClient]);
}

/**
 * Hook to get cached dashboard data without triggering a request
 */
export function useCachedDashboardMetrics(): DashboardMetrics | undefined {
  const queryClient = useQueryClient();
  return queryClient.getQueryData(DASHBOARD_QUERY_KEY);
}